import { ActionCard } from "./ActionCard";
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import {
  RigActivityConfigViewModel,
  StructuredActionsViewModel,
} from "../RigActivitiesFormViewModel";
import { titleCase } from "common/utils/strings";
import { TypedPathTo } from "portal/utils/typedPath";
import { useMuiColor } from "common/hooks/useMuiColor";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import DetailsMenuIcon from "@mui/icons-material/MoreVert";
import MoveDownIcon from "@mui/icons-material/ArrowDownward";
import MoveUpIcon from "@mui/icons-material/ArrowUpward";
import React, { useState } from "react";

export const ActionStage: React.FC<{
  basePath: TypedPathTo<RigActivityConfigViewModel>;
  structuredModel: StructuredActionsViewModel;
  stageIndex: number;
  openNewActionChooser: () => void;
  onEditAction: (actionIndex: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete: () => void;
}> = ({
  structuredModel,
  basePath,
  stageIndex,
  openNewActionChooser,
  onEditAction,
  onMoveUp,
  onMoveDown,
  onDelete,
}) => {
  const { t } = useTranslation();
  const borderColor = useMuiColor("textDisabled");
  const [detailsMenuAnchorEl, setDetailsMenuAnchorEl] = useState<
    Element | undefined
  >();

  return (
    <Paper variant="outlined" className="rounded-xl" style={{ borderColor }}>
      <div
        className="grid grid-cols-[minmax(0,1fr)_min-content] grid-rows-[repeat(2,min-content)] border-solid border-0 border-b p-2"
        style={{ borderBottomColor: borderColor }}
      >
        <Typography variant="h6">{`Stage ${stageIndex + 1}`}</Typography>
        <Typography variant="body2" className="col-start-1">
          {!stageIndex
            ? "Runs at Task Start"
            : `Starts after Stage ${stageIndex} ends`}
        </Typography>
        <IconButton
          className="col-start-2 row-span-full"
          onClick={(e) => setDetailsMenuAnchorEl(e.currentTarget)}
        >
          <DetailsMenuIcon />
        </IconButton>
      </div>
      <div className="flex flex-col gap-2 p-2">
        {(
          structuredModel.actionIndexesRenderingInStageIndex.get(stageIndex) ??
          []
        ).map((actionIndex) => (
          <ActionCard
            key={actionIndex}
            onEdit={() => onEditAction(actionIndex)}
            actionBasePath={basePath.actions[actionIndex]!}
            stageIndex={stageIndex}
            finalActionThisEffector={structuredModel.effectorFinalActionIndexes.has(
              actionIndex
            )}
          />
        ))}
        <Button
          variant="contained"
          color="secondary"
          onClick={openNewActionChooser}
        >
          {t("utils.actionPhrases.newAction")}
        </Button>
      </div>
      <Menu
        anchorEl={detailsMenuAnchorEl}
        open={detailsMenuAnchorEl !== undefined}
        onClose={() => setDetailsMenuAnchorEl(undefined)}
      >
        {onMoveUp && (
          <MenuItem
            onClick={() => {
              setDetailsMenuAnchorEl(undefined);
              onMoveUp();
            }}
          >
            <ListItemIcon>
              <MoveUpIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{t("utils.form.moveUp")}</Typography>
            </ListItemText>
          </MenuItem>
        )}
        {onMoveDown && (
          <MenuItem
            onClick={() => {
              setDetailsMenuAnchorEl(undefined);
              onMoveDown();
            }}
          >
            <ListItemIcon>
              <MoveDownIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{t("utils.form.moveDown")}</Typography>
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setDetailsMenuAnchorEl(undefined);
            onDelete();
          }}
        >
          <ListItemIcon>
            <DeleteIcon color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error">
              {titleCase(t("utils.actions.delete"))}
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};
